import "./index.scss";
import { NavLink } from "hooks/useCustomNavigation";
import { INTERNAL_PATHS, EXTERNAL_LINKS } from "routes/paths";
import { ReactHelmetWithProvider } from "providers/ReactHelmetProvider";

export const PrivacyPolicy = () => (
  <div className="privacy-policy">
    <ReactHelmetWithProvider>
      <title>Raise Marketplace Privacy Policy and Data Protection | GCX</title>
      <meta
        name="description"
        content="Learn how GCX protects your personal information with strict privacy and data protection policies. Buy and sell gift cards confidently and securely."
      />
    </ReactHelmetWithProvider>
    <h1>Privacy Policy and Data Protection</h1>
    <p>Effective Date: July 8, 2024</p>
    <p>
      This Online Privacy Policy applies to information about you that Raise
      Marketplace, LLC (“GCX,” “we,” or “us”) may obtain about you when you
      visit <NavLink to={INTERNAL_PATHS.HOME}>gcx.raise.com</NavLink> or use our
      mobile application (collectively, the “Services”). This Privacy Policy
      also describes how we may collect, use, or share information about you;
      your choices regarding our use of your information; the ways in which we
      safeguard the information we collect; and how you may contact us regarding
      our privacy practices.
    </p>
    <h2>Information We Collect</h2>
    <h3>Information You Provide to Us</h3>
    <p>
      We obtain information that you provide to us directly, for example, data
      you submit when you{" "}
      <NavLink to={INTERNAL_PATHS.USER_SIGN_UP}>create an account</NavLink>. The
      information we collect may include personal information such as your name,
      email address, phone number, and date of birth. In some cases we also may
      request driver’s license or State ID card information or a Tax
      Identification Number to authenticate a user’s identity in conjunction
      with certain transactions.
    </p>
    <h3>
      Information That is Collected Automatically When You Use the Services
    </h3>
    <p>
      In addition to information you submit to us, we may collect certain
      information using automated means such as cookies, web beacons, and web
      server logs. The types of information we collect automatically may include
      IP addresses, device identifiers, browser characteristics, operating
      system details, language preferences, referring URLs, length of visits,
      and pages viewed.
    </p>
    <p>
      In addition, when you use our mobile application we may collect certain
      information that your mobile device captures during normal usage, such as
      motion-generated or orientation-generated mobile sensor data that your
      mobile device uses for various features. For example, when you turn your
      phone and the screen reorients itself based on the way you’re holding it,
      or when you shake your phone to trigger a particular action.
    </p>
    <p>
      We may combine certain automatically-collected information with other
      information we obtain about you.
    </p>
    <h3>Data Analytics</h3>
    <p>
      We also may use third-party web analytics services, such as Google
      Analytics. These service providers help us understand our audience and
      analyze how visitors use the Services. To learn more about how Google may
      use information collected through the Services,{" "}
      <NavLink to={EXTERNAL_LINKS.GOOGLE_PRIVACY_PARTNERS}>click here</NavLink>.
      To learn more about opting out of data collection through Google
      Analytics,{" "}
      <NavLink to={EXTERNAL_LINKS.GOOGLE_ANALYTICS_DISABLE}>click here</NavLink>
      .
    </p>
    <p>
      Your web browser may have settings that allow you to transmit a “Do Not
      Track” signal when you visit various websites or use online services. Like
      many websites and online services, our Services are not designed to
      respond to “Do Not Track” signals received from browsers. To learn more
      about “Do Not Track” signals, you may wish to visit{" "}
      <NavLink to={EXTERNAL_LINKS.DO_NOT_TRACK_INFO}>
        http://www.allaboutdnt.com/
      </NavLink>
      .
    </p>
    <h3>Location Information</h3>
    <p>
      When you use our mobile applications, we may collect, use, and share
      precise location data about you to provide and improve our location-based
      products and services. Where available, location-based services may use
      technologies such as GPS, Bluetooth, crowd-sourced Wi-Fi hotspots, and
      cell tower locations to determine a mobile device’s approximate location.
      You may use your device settings to restrict this type of location
      tracking generally, and/or to prevent a particular app from obtaining
      location data about your device in connection with the use of that app.
    </p>
    <h2>How We May Use Your Information</h2>
    <p>
      We may use the information we collect about you for various purposes,
      including to:
    </p>
    <ul>
      <li>Personalize and improve your online experience;</li>
      <li>
        Respond to emails or other requests, inquiries, comments, or questions;
      </li>
      <li>Provide customer support;</li>
      <li>
        Present you with offers tailored to your interests and past activities
        or usage of the Services;
      </li>
      <li>
        Monitor and analyze trends, usage, and activities of visitors to, and
        users of, the Services;
      </li>
      <li>
        Improve the Services, our marketing, and products or services we may
        offer;
      </li>
      <li>
        Contact you regarding your use of the Services or, in our discretion,
        changes to our policies;
      </li>
      <li>Verify and/or authenticate a user’s identity;</li>
      <li>
        Provide users with information that we believe may be useful to them,
        such as information about products or services offered by us or by
        others;
      </li>
      <li>
        Prevent fraud or other misuse of the Services, including protecting our
        rights and the rights of affiliates or related third parties;
      </li>
      <li>Maintain records of our transactions and communications;</li>
      <li>
        Comply with applicable laws, regulations, or legal process as well as
        industry standards and our company policies;
      </li>
      <li>
        Prevent, investigate, identify, or take any other action with regard to
        any suspected or actual fraudulent or illegal activity or any activity
        that violates our Terms of Use; or
      </li>
      <li>For any other purpose, with your consent.</li>
    </ul>
    <p>
      We also may use the information we obtain about you in other ways for
      which we provide notice at the time of collection.
    </p>
    <p>
      In addition, we use information collected online through cookies, web
      beacons, and other automated means for purposes such as recognizing your
      browsing device when you visit the Services, tracking your activity as you
      navigate the Services, or analyzing your use of the Services. This
      information may be used to track your activity over time on various
      websites and services, as well as across different devices you may use to
      access the Internet and our Services.
    </p>
    <h2>How We May Share Information</h2>
    <p>
      We may permit our agents, vendors, consultants, and other service
      providers to access information we collect about you through the Services
      to carry out work on our behalf. We also may use outside companies to help
      us send emails or text messages and manage email or text message
      campaigns, manage our careers page, or host or operate the Services. These
      third party service providers use the personal information to which they
      have access to carry out the purposes for which we shared the information.
    </p>
    <p>
      We also may share your information: (1) if we are required to do so by
      law, regulation, or legal process (such as in response to a court order or
      subpoena or similar government request for information); (2) to fulfill
      requests by government agencies, including law enforcement authorities;
      (3) when we believe disclosure is necessary or appropriate to prevent
      physical harm or financial loss, or in connection with an investigation of
      suspected or actual illegal activity; (4) to enforce our policies, or to
      protect legal rights, property, or safety; (5) with third parties, to
      investigate or address possible criminal or fraudulent activity; or (6) in
      the event of a joint venture, partnership, merger, or other collaboration
      with another organization.
    </p>
    <p>
      We reserve the right to transfer any information we obtain through the
      Services in the event we sell or transfer all or a portion of our business
      or assets (e.g., further to a merger, reorganization, liquidation, or any
      other business transaction, including negotiations of such transactions).
    </p>
    <h2>Information Security</h2>
    <p>
      We maintain appropriate administrative, technical, and physical safeguards
      designed to help protect personal information collected or received
      through the Services. Although we use reasonable efforts to safeguard
      information, transmission via the Internet is not completely secure and we
      cannot guarantee the security of your information collected through the
      Services.
    </p>
    <h2>Links to Other Websites</h2>
    <p>
      The Services may include links to other websites for your convenience and
      information. Websites that are operated by entities not affiliated with
      GCX may have their own privacy policies or notices, which we strongly
      suggest you review. GCX is not responsible for the content or privacy
      practices of any linked websites that we do not control.
    </p>
    <h2>Social Features</h2>
    <p>
      The Services may include features that are designed to permit interactions
      that you initiate between the Services and third party websites or
      services, including third party social networks (“Social Features”).
      Examples of Social Features include enabling you to “like” or “share” our
      content on other websites or services; make comments on certain pages
      using a plugin; or to transmit content to the Services from your account
      on a third party website or service.
    </p>
    <p>
      If you use Social Features on the Services, both GCX and the third-party
      service that operates the Social Features may have access to certain
      information about you and your use of both the Services and theirs. The
      information we collect in connection with Social Features is subject to
      this Privacy Policy. The information collected and stored by the third
      parties remains subject to those third parties’ privacy practices,
      including whether the third parties continue to share information with us,
      the types of information shared, and your choices with regard to what is
      visible to others on those third party websites or services.
    </p>
    <h2>Our use of Targeted Advertising</h2>
    <p>
      Targeting Cookies helps us and the advertising partners we work with
      gather information about the purchases you made on our Services and other
      websites. This may result in you seeing GCX banners and ads when you are
      on other websites, apps, and social media platforms.
    </p>
    <p>
      We may also target ads using Personal Information we have collected when
      you create an account on the Services. We may disclose certain
      pseudo-anonymized (hashed) portions of your Personal Information (brands
      category) to advertising partners.
    </p>
    <h2>Children’s Privacy</h2>
    <p>
      The Services are not intended for, nor targeted to, children under 13, and
      we do not knowingly or intentionally collect information from children
      under 13. If we learn that we have received information directly from a
      child who is under the age of 13, we will delete the information in
      accordance with applicable law.
    </p>
    <h2>California Privacy Rights</h2>
    <p>
      California law permits customers in California to request certain details
      about how their “personal information” (as defined by California Civil
      Code Section 1798.83) is shared with third parties for direct marketing
      purposes. Under the law, a business must either provide this information
      or permit California customers to opt in to, or opt out of, this type of
      sharing. View our California Consumer Privacy Notice{" "}
      <a
        href={EXTERNAL_LINKS.RAISE_CCPA_NOTICE}
        target="_blank"
        rel="noreferrer"
      >
        here
      </a>
      .
    </p>
    <h2>Your Choices</h2>
    <p>
      You may unsubscribe from receiving promotional emails from us by following
      the instructions provided in those email communications. Please note that
      even if you opt out of receiving promotional communications from us, we
      may continue to send you non-promotional emails, such as communications
      regarding our ongoing relationship with you.
    </p>
    <p>
      Many web browsers provide options to allow you to stop accepting new
      cookies, or to disable existing cookies. Please be aware that if you
      disable the cookies on your device, you may not be able to use certain
      features of the Services or other websites. In addition, disabling cookies
      may invalidate opt outs that use cookies to recognize devices that have
      opted out.
    </p>
    <h2>Information for Users Outside the United States</h2>
    <p>
      As set forth in our{" "}
      <NavLink to={INTERNAL_PATHS.TERMS_OF_USE}>Terms of Use</NavLink>, GCX is
      intended for use in the United States. If you happen to be visiting us
      from outside the United States, please be aware that information we
      collect will be transferred to and stored on our servers in the United
      States. By using the Services, you acknowledge and consent to the transfer
      and processing of your personal data in the United States as described in
      this Privacy Policy. Please be aware that the data protection laws and
      regulations applicable to your personal data transferred to the United
      States may be different from the laws in your country of residence.
    </p>
    <h2>Changes to this Privacy Policy</h2>
    <p>
      We may change this Privacy Policy from time to time. If we make changes,
      we will notify you by revising the date at the top of this Privacy Policy.
      We encourage you to review this Privacy Policy whenever you visit the
      Services to stay informed about our information practices.
    </p>
    <h2>How to Contact Us</h2>
    <address>
      <p>
        If you have questions regarding this Privacy Policy you may email us at{" "}
        <a href="mailto:privacy@gcx.raise.com" target="_blank" rel="noreferrer">
          privacy@gcx.raise.com
        </a>{" "}
        or write to us at:
      </p>
      <p>
        Raise Marketplace, LLC
        <br />
        Attention: Privacy Inquiry
        <br />
        2045 West Grand Avenue
        <br />
        Ste B #61404
        <br />
        Chicago, IL 60612
      </p>
    </address>
  </div>
);
